import type { FrameworkComponent, RiskEntry, SrmModule } from '@types';
import { srmModuleReportData } from '../../functions/src/common/srmModuleReports';

export type SrmDashboardTab = 'overview' | 'actions' | 'components' | 'audit_log';

export const srmDashboardTabs = (readOnly: boolean, pendingActions: number) => [
    { id: 'overview' as const, label: 'Strategic Overview' },
    { id: 'actions' as const, label: `Actions (${pendingActions})` },
    { id: 'components' as const, label: 'Systems & Tools' },
    ...(!readOnly ? [{ id: 'audit_log' as const, label: 'Audit Sequence' }] : []),
];

export const safeSrmDashboardTab = (tab: unknown, readOnly: boolean): SrmDashboardTab => {
    const selected = ['overview', 'actions', 'components', 'audit_log'].includes(String(tab))
        ? String(tab) as SrmDashboardTab
        : 'overview';
    return readOnly && selected === 'audit_log' ? 'overview' : selected;
};

export const visibleSrmRisks = (risks: RiskEntry[], readOnly: boolean) => readOnly
    ? risks.filter(risk => risk.status !== 'Pending')
    : risks;

export function reviewedSrmRiskScore(risks: RiskEntry[]): number | undefined {
    const scores = risks.filter(risk => risk.status !== 'Pending' && Number.isFinite(risk.score) && risk.score >= 0 && risk.score <= 25)
        .map(risk => risk.score).sort((a, b) => b - a).slice(0, 5);
    return scores.length ? Math.round(scores.reduce((sum, score) => sum + score, 0) / scores.length) : undefined;
}

export function srmModuleData(module: SrmModule, components: FrameworkComponent[] = []) {
    const candidates = [module.resultData, (module as SrmModule & { data?: unknown }).data,
        components.find(component => component.id === module.id)?.data];
    for (const data of candidates)
        if (typeof data === 'string' ? data.trim() : data && typeof data === 'object' && Object.keys(data).length)
            return srmModuleReportData(data);
    return undefined;
}

export const linkedSrmRemediationModule = (risk: RiskEntry, modules: SrmModule[]) => {
    const linked = modules.find(module => module.id === risk.targetComponentId);
    if (linked)
        return linked;
    const recommendation = String(risk.recommendation || '').toLowerCase();
    const names = ['fsat', 'geopolitical', 'ep threat', 'crisis', 'information highway',
        'business resiliency', 'emergency', 'facility protection', 'event security'];
    return modules.find(module => names.some(name => recommendation.includes(name) &&
        `${module.title} ${module.workflowId}`.toLowerCase().includes(name)));
};
